import React, { useCallback, useContext } from 'react'
import Grid from '@mui/material/Grid'
import { CollectionType, SearchResultType, TeamMemberRoleEnum } from '../../../lib/types'
import useInfiniteScroll from '../../../components/hooks/useInfiniteScrool'
import { usePageableRequest } from '../../../components/hooks/usePageableRequest'
import Card from '../../../components/Card'
import CollectionCard from '../../../components/CollectionCard'
import Typography from '@mui/material/Typography'
import { RouteComponentProps } from 'react-router-dom'
import api from '../../../lib/api'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { CreateCollectionContext } from '../../../components/CreateCollection'
import { usePermissions } from '../../../components/hooks/usePermissions'
import { UserContext } from '../../../components/UserProvider'

const Collections: React.FC<RouteComponentProps> = () => {
  const { actions: createCollectionActions } = useContext(CreateCollectionContext)
  const { isCreator } = usePermissions()
  const { loading, items, hasNextPage, error, loadMore, setItems } = usePageableRequest<SearchResultType>({
    size: 15,
    q: '',
    request: api.searchCollections
  })

  const { member } = useContext(UserContext)

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore: loadMore,
    disabled: !!error,
    rootMargin: '0px 0px 400px 0px'
  })

  const handleCallback = useCallback(
    (collection: CollectionType | null) => {
      if (!collection) return
      setItems([
        {
          kind: 'COLLECTION',
          title: collection.title,
          id: collection.id
        },
        ...items
      ])
    },
    [items, setItems]
  )

  const permittedCollections = member?.permissions?.map(p => p.collectionId) ?? []
  const filteredItems =
    member?.role === TeamMemberRoleEnum.VIEWER || member?.role === TeamMemberRoleEnum.EDITOR
      ? items.filter(item => permittedCollections.includes(item.id))
      : items

  return (
    <Grid
      container
      sx={{
        padding: '24px'
      }}
      spacing={4}
    >
      <Grid item xs={12}>
        <Typography variant="h6">Collections</Typography>
      </Grid>
      <Grid item xs={12}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ flex: '1' }} />
          <Button
            variant="outlined"
            sx={{ height: '56px' }}
            onClick={() => createCollectionActions.open(handleCallback, undefined)}
            disabled={!isCreator}
          >
            New collection
          </Button>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Grid container>
          {filteredItems.map(item => (
            <CollectionCard key={item.id} data={item} />
          ))}
          {hasNextPage && (
            <Grid item xs>
              <Card loading={true} title={''} subTitle={''} thumbnail={''} preview={''} />
              <div ref={sentryRef} />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}

export default Collections
